import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Eye, EyeOff, Info, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import { ALL, useScope } from '@/lib/current-bot';
import { plural } from '@/lib/client-withdraw';
import type { PartnerHome, PartnerSubscription } from '@/api/types';
import { BotAvatar, BotPicker } from '@/components/BotPicker';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { Skeleton, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';

/**
 * Сводка (макет A, утверждён 17.10.2026). Одна главная цифра: прибыль за период, под
 * ней вывод денег, что требует внимания и лента последних продаж по дням. Раньше здесь
 * была зелёная карточка баланса со свечением, плитки и сетка быстрых действий: всё
 * важное было одного веса, и глаз не знал, куда смотреть.
 *
 * Стекло (чип бота, кнопка «скрыть суммы», переключатель периода) то же, что у нижней
 * панели. Цифры по выбранному боту (lib/current-bot), «Все боты» при двух и больше.
 */
export const Route = createFileRoute('/dashboard')({ component: Summary });

const PERIODS = [
  { days: 7, short: '7 д', label: 'за 7 дней', prev: 'к прошлым 7 дням' },
  { days: 30, short: '30 д', label: 'за 30 дней', prev: 'к прошлым 30 дням' },
  { days: 365, short: 'Год', label: 'за год', prev: 'к прошлому году' },
] as const;

// Неполный ответ не должен печатать «не число ₽». Между числом и ₽ неразрывный пробел.
const rub = (n: number) => `${Math.round(Number.isFinite(n) ? n : 0).toLocaleString('ru')} ₽`;

function Summary() {
  const navigate = useNavigate();
  const [days, setDays] = useState<7 | 30 | 365>(30);
  const [hidden, setHidden] = useState(false);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const meQ = useQuery({ queryKey: ['partner-me'], queryFn: () => api.me(), placeholderData: keepPreviousData });
  const botsQ = useQuery({ queryKey: ['bots'], queryFn: () => api.bots(), staleTime: 60_000 });
  const bots = botsQ.data ?? [];
  const { scope, bot } = useScope(bots);
  // Один бот: «все» и «этот» совпадают, фильтр не шлём (у старых подписок bot_id пуст).
  const botFilter = scope && scope !== ALL && bots.length > 1 ? scope : null;

  const statsQ = useQuery({
    queryKey: ['partner-dashboard-stats', days, botFilter],
    queryFn: () => api.dashboardStats(days, botFilter),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const homeQ = useQuery({
    queryKey: ['partner-home', botFilter],
    queryFn: () => api.partnerHome(botFilter),
    staleTime: 60_000,
  });
  const salesQ = useQuery({
    queryKey: ['partner-recent-sales', botFilter],
    queryFn: () => api.subscriptions({ limit: 8, kind: 'paid', botId: botFilter ?? undefined }),
    staleTime: 30_000,
  });

  if ((meQ.isPending && !meQ.data) || (botsQ.isPending && !botsQ.data)) return <SummarySkeleton />;

  const stats = statsQ.data;
  const period = PERIODS.find((p) => p.days === days) ?? PERIODS[1];
  const profit = stats?.margin_rub ?? 0;
  const trend = stats?.margin_change_percent ?? null;
  const balance = meQ.data?.balanceRub ?? 0;
  const deposit = meQ.data?.paidRub ?? 0;
  const mask = (text: string) => (hidden ? '••• •••' : text);

  return (
    <div className="p-4 pb-6">
      {/* Шапка: выбранный бот и «скрыть суммы» */}
      <div className="flex items-center justify-between gap-3 pt-1">
        {bots.length === 0 ? (
          <Link
            to="/onboarding/bot"
            onClick={() => haptic('light')}
            className="glass glass-flat inline-flex min-w-0 items-center gap-2 rounded-full py-2 pl-3 pr-4 text-[14px] font-semibold"
          >
            <Plus className="size-4" /> Создать бота
          </Link>
        ) : (
          <button
            type="button"
            disabled={bots.length < 2}
            onClick={() => {
              haptic('light');
              setPicking(true);
            }}
            className="flex min-w-0 items-center gap-2.5 text-left disabled:cursor-default"
          >
            {bot && <BotAvatar bot={bot} size={36} />}
            <span className="min-w-0">
              <span className="flex items-center gap-1">
                <span className="truncate font-display text-[22px] font-medium leading-tight">
                  {bot ? bot.brandName || bot.username : 'Все боты'}
                </span>
                {bots.length > 1 && <ChevronDown className="size-[18px] shrink-0 text-dim" />}
              </span>
              <span className="flex items-center gap-1.5 text-[12.5px] text-dim">
                {bot ? (
                  <>
                    <span className={`size-1.5 rounded-full ${bot.status === 'active' ? 'bg-money' : 'bg-white/30'}`} />
                    {bot.status === 'active' ? 'работает' : 'на паузе'}
                  </>
                ) : (
                  `${bots.length} ${plural(bots.length, 'бот', 'бота', 'ботов')}, вместе`
                )}
              </span>
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            haptic('selection');
            setHidden((v) => !v);
          }}
          aria-label={hidden ? 'Показать суммы' : 'Скрыть суммы'}
          className="grid size-10 shrink-0 place-items-center rounded-full text-muted-foreground active:bg-white/[0.06]"
        >
          {hidden ? <EyeOff className="size-[19px]" strokeWidth={1.75} /> : <Eye className="size-[19px]" strokeWidth={1.75} />}
        </button>
      </div>

      <div className="mt-3">
        <OnboardingChecklist />
      </div>

      {/* Главная цифра и три счётчика под ней */}
      <section className="surface-card mt-4 rounded-[20px] p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-dim">Прибыль {period.label}</span>
          {/* Период живёт в карточке, которую он меняет. */}
          <div role="tablist" aria-label="Период" className="flex rounded-[10px] bg-white/[0.06] p-[3px]">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                type="button"
                role="tab"
                aria-selected={days === p.days}
                onClick={() => {
                  haptic('selection');
                  setDays(p.days);
                }}
                className={`h-7 rounded-[7px] px-2.5 text-[12.5px] transition-colors duration-100 ${
                  days === p.days ? 'bg-white/[0.14] font-medium text-foreground' : 'text-dim'
                }`}
              >
                {p.short}
              </button>
            ))}
          </div>
        </div>
        {statsQ.isPending && !stats ? (
          <Skeleton className="mt-2 h-[44px] w-44 rounded-xl" />
        ) : (
          <div className="mt-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className={`font-display text-[40px] font-light leading-none tracking-[-0.02em] nums ${profit < 0 && !hidden ? 'text-danger' : ''}`}>
              {hidden ? (
                '••• •••'
              ) : (
                <>
                  {Math.round(profit).toLocaleString('ru')}
                  <span className="ml-1 text-[22px] text-muted-foreground">₽</span>
                </>
              )}
            </span>
            {trend !== null && !hidden && (
              <span
                className={`rounded-full px-2 py-0.5 text-[12.5px] font-medium nums ${
                  trend >= 0 ? 'bg-[rgba(52,211,153,0.12)] text-money' : 'bg-[rgba(248,113,113,0.12)] text-danger'
                }`}
              >
                {trend >= 0 ? '+' : '−'}
                {Math.abs(trend)}&nbsp;%
              </span>
            )}
          </div>
        )}
        <div className="mt-4 grid grid-cols-3 divide-x divide-white/[0.07] border-t border-white/[0.07] pt-3.5">
          <Metric label="Продажи" value={stats ? String(stats.new_subs + stats.repeat_sales) : null} />
          <Metric label="Новые клиенты" value={stats ? String(stats.new_clients) : null} />
          <Metric label="Подписки сейчас" value={stats ? String(stats.active_subs) : null} />
        </div>
      </section>

      <DailyBars data={stats?.mrr_14d} hidden={hidden} pending={statsQ.isPending && !stats} />

      {/* К выводу */}
      <section className="surface-card mt-3 flex items-center justify-between gap-3 rounded-[20px] px-4 py-3.5">
        <div className="min-w-0">
          <div className="text-[12.5px] text-dim">К выводу</div>
          <div className={`truncate font-display text-[22px] nums ${balance < 0 && !hidden ? 'text-danger' : ''}`}>
            {mask(rub(balance))}
          </div>
          {deposit > 0 && !hidden && (
            <div className="mt-0.5 text-[12px] text-dim nums">и {rub(deposit)} на депозите для ваучеров и призов</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('light');
            void navigate({ to: '/finance' });
          }}
          className="btn-glass-red h-10 shrink-0 rounded-[12px] px-[18px] text-[14.5px] font-semibold active:scale-[0.97] transition-transform"
        >
          Вывести
        </button>
      </section>

      <Attention home={homeQ.data} rate={stats?.fx_rate ?? 0} />

      {/* Продажи */}
      <section className="mt-6">
        <div className="mb-1 flex items-baseline justify-between px-0.5">
          <h2 className="font-display text-[17px] font-medium">Последние продажи</h2>
          <Link to="/subscriptions" onClick={() => haptic('light')} className="text-[13px] text-dim">
            Все ›
          </Link>
        </div>
        <Sales q={salesQ} hidden={hidden} />
      </section>

      {bots.length > 1 && (
        <BotPicker open={picking} onClose={() => setPicking(false)} bots={bots} current={scope} withAll />
      )}
    </div>
  );
}

/* ---------- Лента продаж по дням ---------- */

function dayLabel(d: Date): string {
  const today = new Date();
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(today) - start(d)) / 86_400_000);
  if (diff === 0) return 'Сегодня';
  if (diff === 1) return 'Вчера';
  return d.toLocaleDateString('ru', { day: 'numeric', month: 'long' });
}

function Sales({
  q,
  hidden,
}: {
  q: { data?: { items: PartnerSubscription[] }; isPending: boolean; isError: boolean };
  hidden: boolean;
}) {
  if (q.isPending && !q.data) {
    return (
      <div className="space-y-3 pt-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <SkeletonLine className="w-[40%]" />
              <SkeletonLine size="sm" className="w-[30%]" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  // Упавший запрос не равен «продаж нет».
  if (q.isError && !q.data) {
    return <p className="pt-3 text-sm text-dim">Не удалось загрузить продажи.</p>;
  }
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return <p className="pt-3 text-sm text-dim">Продаж пока нет. Первая появится здесь сразу после оплаты.</p>;
  }
  const groups: { label: string; rows: PartnerSubscription[] }[] = [];
  for (const s of items) {
    const label = dayLabel(new Date(s.createdAt));
    const g = groups[groups.length - 1];
    if (g && g.label === label) g.rows.push(s);
    else groups.push({ label, rows: [s] });
  }
  return (
    <div>
      {groups.map((g) => (
        <div key={g.label}>
          <div className="border-b border-white/[0.07] px-0.5 pb-1.5 pt-4 text-[12px] text-dim">{g.label}</div>
          {g.rows.map((s) => {
            const name = s.clientName || 'Клиент';
            const initial = (name.match(/[\p{L}\p{N}]/u)?.[0] ?? '?').toUpperCase();
            const time = new Date(s.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
            return (
              <div key={s.id} className="flex items-center gap-3 px-0.5 py-[11px]">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-white/[0.08] text-[14px] font-semibold">
                  {initial}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14.5px] font-medium">{name}</span>
                  <span className="block truncate text-[12.5px] text-dim">
                    {s.planName} · {time}
                  </span>
                </span>
                <span
                  className={`shrink-0 text-[14.5px] font-medium nums ${
                    hidden ? 'text-dim' : s.partnerEarnedRub > 0 ? 'text-money' : s.partnerEarnedRub < 0 ? 'text-danger' : 'text-dim'
                  }`}
                >
                  {hidden ? '•••' : `${s.partnerEarnedRub > 0 ? '+' : s.partnerEarnedRub < 0 ? '−' : ''}${rub(Math.abs(s.partnerEarnedRub))}`}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ---------- Три счётчика и столбики по дням ---------- */

function Metric({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0 px-2 first:pl-0 last:pr-0">
      <div className="truncate text-[12px] text-dim">{label}</div>
      {value === null ? (
        <Skeleton className="mt-1 h-6 w-10 rounded-md" />
      ) : (
        <div className="mt-0.5 font-display text-[20px] nums">{value}</div>
      )}
    </div>
  );
}

/**
 * Прибыль по дням за 14 дней. Ряд бэк отдаёт всегда за 14 дней, поэтому подпись своя,
 * а не период сверху. День без продаж рисуется тонкой чертой. Если продаж не было все
 * 14 дней, графика нет вовсе: карточка из одних черт это пустое место на пол-экрана.
 */
function DailyBars({ data, hidden, pending }: { data?: { day: string; margin: number }[]; hidden: boolean; pending: boolean }) {
  if (pending) return <Skeleton className="mt-3 h-[132px] w-full rounded-[20px]" />;
  const rows = data ?? [];
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => r.margin), 0);
  if (max <= 0) return null;
  const fmt = (d: string) => new Date(d).toLocaleDateString('ru', { day: 'numeric', month: 'short' }).replace('.', '');
  return (
    <section className="surface-card mt-3 rounded-[20px] px-4 pb-3 pt-3.5" aria-label="Прибыль по дням">
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] text-dim">По дням, 14 дней</span>
      </div>
      <div className="mt-3 flex h-[64px] items-end gap-[5px]">
        {rows.map((r) => {
          const h = hidden ? 0 : Math.max(0, r.margin) / max;
          return (
            <span key={r.day} className="flex h-full flex-1 items-end" title={`${fmt(r.day)}: ${rub(r.margin)}`}>
              {h > 0 ? (
                <span className="w-full rounded-[4px] bg-white/80" style={{ height: `${Math.max(8, h * 100)}%` }} />
              ) : (
                <span className="h-[3px] w-full rounded-full bg-white/[0.12]" />
              )}
            </span>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-dim">
        <span>{fmt(rows[0].day)}</span>
        <span>сегодня</span>
      </div>
    </section>
  );
}

/* ---------- Нужно сделать ---------- */

type Item = PartnerHome['attention'][number];
type Row = {
  key: string;
  tone: 'danger' | 'warn' | 'info';
  title: string;
  hint?: string;
  action: string;
  to: string;
  params?: object;
  search?: object;
};

const at = (a: Item) => (a.bot_username ? `@${a.bot_username}` : 'бот');
const botsWord = (n: number) => `${n} ${plural(n, 'бот', 'бота', 'ботов')}`;

function describe(a: Item, rate: number): Row | null {
  const bot = at(a);
  const botTo = a.bot_id ? { to: '/bots/$botId', params: { botId: a.bot_id } } : { to: '/bot' };
  const key = `${a.kind}:${a.bot_id ?? ''}:${a.ref_id ?? ''}`;
  switch (a.kind) {
    case 'bot_paused':
      return {
        key,
        tone: 'danger',
        title: `${bot} на паузе`,
        hint: `${a.count} ${plural(a.count, 'живая подписка', 'живые подписки', 'живых подписок')}, ${rub(
          ((a.amount_usd_minor ?? 0) * rate) / 100,
        )} не выводятся`,
        action: 'Включить',
        ...botTo,
      };
    case 'selling_at_cost':
      return {
        key,
        tone: 'danger',
        title: `${bot} продаёт без наценки`,
        hint: 'По цене платформы, вы ничего не зарабатываете',
        action: 'Наценка',
        to: '/pricing',
        search: a.bot_id ? { botId: a.bot_id } : undefined,
      };
    case 'reviews_unanswered':
      return {
        key,
        tone: 'warn',
        title: `${a.count} ${plural(a.count, 'отзыв', 'отзыва', 'отзывов')} без ответа`,
        hint: `${bot}, оценка 3 и ниже`,
        action: 'Ответить',
        ...(a.bot_id ? { to: '/bot-reviews/$botId', params: { botId: a.bot_id } } : { to: '/bot' }),
      };
    case 'client_withdrawals':
      return {
        key,
        tone: 'warn',
        title: 'Клиенты ждут вывода',
        hint: `${a.count} ${plural(a.count, 'заявка', 'заявки', 'заявок')} на реферальные деньги`,
        action: 'Открыть',
        to: '/client-withdrawals',
      };
    case 'payout_rejected':
      return { key, tone: 'danger', title: 'Вывод отклонён', hint: 'Деньги вернулись на баланс', action: 'Причина', to: '/payouts' };
    case 'payout_pending':
      return {
        key,
        tone: 'info',
        title: 'Вывод в обработке',
        hint: a.count > 1 ? `${a.count} ${plural(a.count, 'заявка', 'заявки', 'заявок')}` : undefined,
        action: 'Статус',
        to: '/payouts',
      };
    case 'import_payment':
      return { key, tone: 'warn', title: 'Переезд ждёт оплаты', hint: 'Перенос подписок клиентов', action: 'Оплатить', to: '/bot-import' };
    case 'site_page_rejected':
      return {
        key,
        tone: 'warn',
        title: 'Страница витрины не прошла проверку',
        hint: bot,
        action: 'Открыть',
        ...(a.bot_id ? { to: '/bots/$botId', params: { botId: a.bot_id }, search: { open: 'site' } } : { to: '/bot' }),
      };
    case 'site_domain_failed':
      return {
        key,
        tone: 'warn',
        title: `Домен ${a.ref_id ?? ''} не подключился`,
        hint: 'DNS не смотрит на наш сервер',
        action: 'Проверить',
        ...(a.bot_id ? { to: '/bots/$botId', params: { botId: a.bot_id }, search: { open: 'site' } } : { to: '/bot' }),
      };
    default:
      return null;
  }
}

/** Одно и то же у нескольких ботов сводится в строку: две красные плашки с одинаковым
 * текстом читались как дубль, а кнопка вела только к одному боту. */
function merged(kind: string, items: Item[], rate: number): Row | null {
  const names = items.map(at).join(', ');
  const key = `${kind}:many`;
  if (kind === 'selling_at_cost')
    return { key, tone: 'danger', title: `${botsWord(items.length)} продают без наценки`, hint: names, action: 'Наценка', to: '/pricing' };
  if (kind === 'bot_paused') {
    const subs = items.reduce((n, a) => n + a.count, 0);
    const held = items.reduce((n, a) => n + (a.amount_usd_minor ?? 0), 0);
    return {
      key,
      tone: 'danger',
      title: `${botsWord(items.length)} на паузе`,
      hint: `${subs} ${plural(subs, 'живая подписка', 'живые подписки', 'живых подписок')}, ${rub((held * rate) / 100)} не выводятся`,
      action: 'Боты',
      to: '/bot',
    };
  }
  if (kind === 'reviews_unanswered') {
    const n = items.reduce((k, a) => k + a.count, 0);
    return { key, tone: 'warn', title: `${n} ${plural(n, 'отзыв', 'отзыва', 'отзывов')} без ответа`, hint: names, action: 'Ответить', to: '/bot' };
  }
  return null;
}

const ORDER = { danger: 0, warn: 1, info: 2 } as const;
const TONE = {
  danger: 'bg-[rgba(248,113,113,0.14)] text-danger',
  warn: 'bg-[rgba(240,176,78,0.14)] text-amber',
  info: 'bg-white/[0.07] text-muted-foreground',
} as const;

function Attention({ home, rate }: { home?: PartnerHome; rate: number }) {
  const byKind = new Map<string, Item[]>();
  for (const a of home?.attention ?? []) byKind.set(a.kind, [...(byKind.get(a.kind) ?? []), a]);
  const rows: Row[] = [];
  for (const [kind, items] of byKind) {
    const m = items.length > 1 ? merged(kind, items, rate) : null;
    if (m) rows.push(m);
    else for (const a of items) {
      const r = describe(a, rate);
      if (r) rows.push(r);
    }
  }
  if (rows.length === 0) return null;
  rows.sort((x, y) => ORDER[x.tone] - ORDER[y.tone]);
  return (
    <section className="mt-6" aria-label="Нужно сделать">
      <h2 className="mb-2 px-0.5 font-display text-[17px] font-medium">
        Нужно сделать <span className="text-dim">{rows.length}</span>
      </h2>
      <div className="surface-card divide-y divide-white/[0.06] overflow-hidden rounded-[20px]">
        {rows.map((r) => (
          <Link
            key={r.key}
            to={r.to}
            params={r.params as never}
            search={r.search as never}
            onClick={() => haptic('light')}
            className="flex items-center gap-3 px-4 py-3 active:bg-white/[0.03]"
          >
            <span className={`grid size-8 shrink-0 place-items-center rounded-full ${TONE[r.tone]}`}>
              {r.tone === 'info' ? <Info className="size-4" /> : <AlertTriangle className="size-4" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 block text-[14.5px] font-medium leading-snug">{r.title}</span>
              {r.hint && <span className="block truncate text-[12.5px] text-dim">{r.hint}</span>}
            </span>
            <span className="flex shrink-0 items-center text-[13.5px] text-muted-foreground">
              {r.action}
              <ChevronRight className="size-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------- Скелетон: та же раскладка ---------- */

function SummarySkeleton() {
  return (
    <SkeletonPage className="p-4 pb-6">
      <div className="flex items-center justify-between pt-1">
        <Skeleton className="h-11 w-44 rounded-xl" />
        <Skeleton className="size-10 rounded-full" />
      </div>
      <Skeleton className="mt-4 h-[150px] w-full rounded-[20px]" />
      <Skeleton className="mt-3 h-[132px] w-full rounded-[20px]" />
      <Skeleton className="mt-3 h-[76px] w-full rounded-[20px]" />
      <SkeletonLine size="lg" className="mt-7 w-44" />
      <div className="mt-4 space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <SkeletonLine className="w-[40%]" />
              <SkeletonLine size="sm" className="w-[30%]" />
            </div>
          </div>
        ))}
      </div>
    </SkeletonPage>
  );
}
